import { weekendLabel } from '@/lib/planning/weekend'
import { letGoNote } from '@/lib/planning/letGo'
import { useCallback, useRef, useState, type ReactNode, useEffect } from 'react'
import type { Task, TaskContext } from '@/types/task'
import type { Contact } from '@/types/contact'
import type { Project } from '@/types/project'
import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { FamilyMember } from '@/types/family'
import { PanelShell } from './PanelShell'
import { PanelHeader } from './sections/PanelHeader'
import { PanelActions, type PanelAction } from './sections/PanelActions'
import { PanelMoreMenu } from './sections/PanelMoreMenu'
import { SchedulePicker } from '@/components/schedule/SchedulePicker'
import { useDayLoads } from './hooks/useDayLoads'
import { PanelAssistant } from './sections/PanelAssistant'
import { PanelNotes } from './sections/PanelNotes'
import { PanelSource } from './sections/PanelSource'
import { PanelSubtasks } from './sections/PanelSubtasks'
import { ChoicesSection } from '@/components/choices/ChoicesSection'
import { GoalLogSection } from '@/components/choices/GoalLogSection'
import { PanelPeople } from './sections/PanelPeople'
import { PanelLinked } from './sections/PanelLinked'
import { PanelLinks } from './sections/PanelLinks'
import { PanelPhotos } from './sections/PanelPhotos'
import { PanelConversations } from './sections/PanelConversations'
import { PanelLocation } from './sections/PanelLocation'
import type { PrepField } from '@/components/canvas/prep/ReadinessSection'
import { PanelFacts } from './sections/PanelFacts'
import { foldedClick } from './sections/PanelActions'
import { ContextPicker } from '@/components/triage/ContextPicker'
import { MultiAssigneeDropdown } from '@/components/family'
import { PanelReach } from './sections/PanelReach'
import { PanelMightBeRelevant } from './sections/PanelMightBeRelevant'
import { PanelFooter } from './sections/PanelFooter'
import { PanelToBuySuggestion } from './sections/PanelToBuySuggestion'
import { PanelSection } from './sections/PanelSection'
import { WaitingForPopover } from '@/components/schedule/WaitingForPopover'
import { useLinkedEntities } from './hooks/useLinkedEntities'
import { useMightBeRelevant } from './hooks/useMightBeRelevant'
import { AssistDrawer } from '@/components/assist/AssistDrawer'
import { useItemDiscussionLabel } from '@/hooks/useItemDiscussionLabel'
import { useThreadUnread } from '@/hooks/useThreadUnread'
import { useFamilyMembers } from '@/hooks/useFamilyMembers'
import { scopeForDomain } from '@/lib/scope'
import type { MightBeRelevantItem } from './types'
import type { Goal } from '@/types/goal'
import type { Attachment } from '@/lib/taskAttachments'
import { ReadinessSection } from '@/components/canvas/prep/ReadinessSection'

/** The fields a task can carry that aren't set yet — each opened from a
 *  "What you'll need" chip. */
type AddableField = PrepField

interface TapContextPanelProps {
  /**
   * The task's own when — the same control its row carries — under the title.
   * The pane named no date at all (S2-26) and hid "remove the day" at the
   * bottom of the Schedule popover (S2-27). Supplied by the host, which owns
   * the writer; omitted, the pane is as it was.
   */
  timingControl?: ReactNode
  /** The goal this task serves — the value of the Goal row under the title. */
  purpose?: ReactNode
  /** "Make it a goal", offered in the ⋯. Omitted where it can't apply. */
  onMakeGoal?: () => void
  task: Task
  // Reference data (Plan 1: passed in by caller — Plan 2 may push into context)
  contacts: Contact[]
  projects: Project[]
  events: CalendarEvent[]
  familyMembers: FamilyMember[]
  siblingTaskCandidates: Task[]
  allTasks: Task[]
  /** Yearly intentions, so "What you'll need" can show what the plan above carries. */
  goals?: Goal[]
  /** Optional why-chain (Task → Project → Goal), rendered under the title. */
  whyChain?: ReactNode
  /** Optional creator name for the meta row + footer. */
  createdByName?: string

  // Handlers
  onClose: () => void
  onTitleChange: (next: string) => void
  /** Resolve `true` once written for the Notes section to say Saved. */
  onNotesChange: (next: string) => unknown
  /** Promote the task's notes into a persisting vault note linked to the task. */
  onSaveNoteToVault?: (content: string) => Promise<{ ok: boolean; url?: string }>
  onToggleComplete: () => void
  onSchedule: (date: Date, isAllDay: boolean) => void
  onReschedule?: (when: import('@/components/schedule/TriageWhenMenu').TriageWhen) => void
  onClearSchedule?: () => void
  isPinned: boolean
  onTogglePin: () => void
  onDelete: () => void
  onOpenContact: (id: string) => void
  onOpenMember: (id: string) => void
  onOpenProject: (id: string) => void
  onOpenEvent: (id: string) => void
  onOpenTask: (id: string) => void
  onOpenRelated: (kind: MightBeRelevantItem['kind'], id: string) => void
  onToggleSubtask: (id: string) => void
  onAddSubtask: (title: string) => void
  /** Detach a subtask from this group (becomes standalone). Group management only. */
  onRemoveSubtask?: (id: string) => void
  /** Triage a single subtask (relative when). */
  onRescheduleSubtask?: (id: string, when: import('@/components/schedule/TriageWhenMenu').TriageWhen) => void
  /** Triage a single subtask to a specific date/time. */
  onScheduleSubtask?: (id: string, date: Date, isAllDay: boolean) => void
  /** Dissolve this group, keeping the tasks. Present only when the task has subtasks. */
  onUngroup?: () => void
  /** Delete this group and all its tasks. Present only when the task has subtasks. */
  onDeleteGroup?: () => void
  onAddLink: (url: string) => void
  /** Set/clear the tap-to-call number carried by this task. */
  onPhoneChange: (next: string | undefined) => void
  /** Set/clear the tap-to-mail address carried by this task. */
  onEmailChange: (next: string | undefined) => void
  onUpdateLocation: (location: string, placeId?: string) => void
  onClearLocation: () => void
  /** Persist the task's route (origin/stops/mode). Omit to keep directions ephemeral. */
  onDirectionsChange?: (directions: import('@/types/directions').TaskDirections) => void
  onContextChange: (context: TaskContext | undefined) => void
  onAssigneesChange: (ids: string[]) => void
  /** Link/change/clear the task's related contact. When omitted, the People section is read-only. */
  onContactChange?: (contactId: string | undefined) => void
  onSearchContacts?: (query: string) => Contact[]
  onAddContact?: (name: string, details?: { phone?: string; category?: import('@/types/contact').ContactCategory; placeId?: string }) => Promise<Contact | null>
  /** Refetch after the planning assistant writes (enables the Help-me-plan action). */
  onAssistMutate?: () => void
  /** Open the Discussion on mount (deep link from the Discussions inbox). */
  autoOpenDiscussion?: boolean
  /** Convert a buy-ish task into a "To buy" list item. When wired, the panel
   *  offers it as one quiet line under the actions — the suggestion lives
   *  here, not on Today's page. */
  onSendToBuy?: () => void

  // ── The jobs a Today row's menus used to do (Today, trimmed, 2026-10-14).
  // A row is tick, drag and tap now; everything else it offered lives here.
  // Each is optional: a host that doesn't wire one gets no chip for it.

  /** "Bring up" — flag for a conversation in person (the wall's For
   *  Discussion list, Today's To talk about line), and the question. */
  onUpdateDiscussion?: (next: { needsDiscussion: boolean; discussionNote?: string }) => unknown
  /** "Waiting for…" — the sentence and an optional check-back day. */
  onSaveWaiting?: (waitingFor: string, checkBack?: Date) => unknown
  /** Ends the wait. Offered only while the task is waiting. */
  onClearWaiting?: () => unknown
  /** Marked needed on the day being viewed. */
  neededToday?: boolean
  /** Toggles that mark for the viewed day. */
  onToggleNeededToday?: () => unknown
  /** Takes the viewed day away, keeping the week and period lists. Passed
   *  only when the task is on that day. */
  onRemoveFromDay?: () => unknown
  /** What that removal says, e.g. "Remove from today". */
  removeFromDayLabel?: string
  /** Opened from a Month or Season page: "Move to November", "Remove from
   *  October" (horizonPanel.tsx). Listed in the ⋯ with removing the day. */
  periodActions?: { id: string; label: string; onClick: () => void }[]
  /** A let-go item (found through search): "Let go Nov 4 · Bring back"
   *  puts back what it had and clears the mark (unletGoUpdates). */
  onBringBack?: () => unknown
}

export function TapContextPanel(props: TapContextPanelProps) {
  const { task, allTasks, createdByName } = props

  const [showDirections, setShowDirections] = useState(false)
  const [assistOpen, setAssistOpen] = useState(props.autoOpenDiscussion === true)
  const [waitingOpen, setWaitingOpen] = useState(false)
  useEffect(() => { if (props.autoOpenDiscussion) setAssistOpen(true) }, [props.autoOpenDiscussion])
  const discussionUnread = useThreadUnread('task', task.id)
  const discussionLabel = useItemDiscussionLabel('task', task.id)

  // The Discuss thread is shared exactly as widely as the task is: its scope is
  // DERIVED from the task's domain + assignees, never picked. Self is whoever
  // OWNS the task, so a partner opening a task handed to her doesn't compute
  // "no other assignees" and silently narrow the thread to individual.
  const { getCurrentUserMember } = useFamilyMembers()
  const selfMemberId = getCurrentUserMember()?.id ?? null
  const discussScope = scopeForDomain(
    task.context,
    [task.assignedTo, ...(task.assignedToAll ?? [])],
    selfMemberId,
  )

  // Which empty fields the user asked to fill in. Cleared when the panel
  // switches tasks — revealing Notes on one task shouldn't open it on the next.
  const [revealed, setRevealed] = useState<Set<AddableField>>(() => new Set())
  // Attachments are fetched inside PanelPhotos; it reports up so the Add row
  // knows whether to offer "Photo".
  const [photosHaveContent, setPhotosHaveContent] = useState(false)
  const [files, setFiles] = useState<Attachment[]>([])
  const reveal = useCallback((field: AddableField) => {
    setRevealed((prev) => new Set(prev).add(field))
  }, [])

  // Reset per-task panel state when switching tasks (React-recommended
  // "adjust state during render" pattern, not an effect).
  const [prevTaskId, setPrevTaskId] = useState(task.id)
  if (task.id !== prevTaskId) {
    setPrevTaskId(task.id)
    setShowDirections(false)
    setAssistOpen(false)
    setWaitingOpen(false)
    setRevealed(new Set())
    setPhotosHaveContent(false)
    setFiles([])
  }

  const linked = useLinkedEntities(task, {
    contacts: props.contacts,
    projects: props.projects,
    events: props.events,
    familyMembers: props.familyMembers,
    siblingTaskCandidates: props.siblingTaskCandidates,
  })

  const mightBeRelevant = useMightBeRelevant(task, { allTasks })

  // A section earns its header by having something in it. Everything else
  // collapses into the single Add row near the bottom.
  const has = {
    phone: !!task.phoneNumber,
    email: !!task.email,
    location: !!(task.location || task.locationPlaceId),
    notes: !!task.notes?.trim(),
    subtask: (task.subtasks?.length ?? 0) > 0,
    link: (task.links?.length ?? 0) > 0,
    person: !!linked.contact,
    // Photos load inside PanelPhotos, so emptiness isn't knowable here — that
    // section hides itself and reports back.
    photo: photosHaveContent,
  }
  const show = (field: AddableField): boolean => has[field] || revealed.has(field)

  // The whole panel accepts file drops, not just the Photos & files section
  // — that section was 16% of the panel, and a miss navigated the tab to
  // the file instead of attaching it.
  const panelRef = useRef<HTMLElement>(null)

  // A readiness chip opens the field's existing editor and brings it into view.
  const focusField = useCallback((field: AddableField | 'directions') => {
    requestAnimationFrame(() => {
      const host = panelRef.current?.querySelector<HTMLElement>(`[data-prep-field="${field === 'directions' ? 'location' : field}"]`)
      if (!host) return
      host.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
      if (field === 'directions') return
      const target = host.querySelector<HTMLElement>('textarea, input:not([type="hidden"]):not([type="file"]), [contenteditable="true"]')
      target?.focus({ preventScroll: true })
    })
  }, [])
  const addPrep = useCallback((field: PrepField) => { reveal(field); focusField(field) }, [reveal, focusField])

  const dayLoads = useDayLoads({ tasks: allTasks, enabled: true })

  const phone = task.phoneNumber || linked.contact?.phone || linked.project?.phoneNumber
  const hasGroup = (task.subtasks?.length ?? 0) > 0

  // Fixed order — complete, then how to reach it, then help. The renderer
  // folds anything past the third into the ⋯. Schedule is not here: it sits
  // in the When row beside the date it changes.
  const actions: PanelAction[] = [
    {
      id: 'complete',
      label: task.completed ? 'Completed' : 'Complete',
      kind: task.completed ? 'completed' : 'primary',
      onClick: props.onToggleComplete,
    },
    // "Call", not the number: the number is listed in What you'll need, and
    // as the label it pushed the row onto a second line.
    ...(phone ? [{ id: 'call', label: 'Call', title: phone, icon: 'call' as const, href: `tel:${phone}` }] : []),
    ...(task.location
      ? [{
          id: 'directions',
          label: 'Directions',
          icon: 'location' as const,
          onClick: () => setShowDirections((v) => !v),
        }]
      : []),
    ...(props.onAssistMutate
      ? [{
          id: 'assist',
          label: discussionLabel,
          icon: 'discussion' as const,
          dot: discussionUnread,
          onClick: () => setAssistOpen(true),
        }]
      : []),
    // After the ones that were always here, so they fold into the ⋯ first
    // rather than pushing Discussion out of sight.
    ...(props.onSaveWaiting
      ? [{
          id: 'waiting',
          label: task.isWaiting ? 'Waiting' : 'Waiting for…',
          kind: (task.isWaiting ? 'flagged' : 'default') as PanelAction['kind'],
          pressed: !!task.isWaiting,
          title: task.isWaiting && task.waitingFor
            ? `Waiting on ${task.waitingFor}`
            : 'Did your part — waiting on someone, check back later',
          onClick: () => setWaitingOpen((v) => !v),
        }]
      : []),
    // "Bring up" is the discussion itself now (discussions v2): starting one
    // puts the task on To talk about, and settling it takes it off.
    ...(props.onToggleNeededToday && !task.completed
      ? [{
          id: 'needed-today',
          label: props.neededToday ? 'Needed today' : 'Need today',
          kind: (props.neededToday ? 'flagged' : 'default') as PanelAction['kind'],
          pressed: !!props.neededToday,
          title: props.neededToday ? 'Clear the needed-today mark' : 'Mark it needed today',
          onClick: () => { void props.onToggleNeededToday?.() },
        }]
      : []),
  ]
  // The ⋯ holds what the row had no room for, then removing the day — the row
  // menu's quiet way out — before Pin and Delete.
  const moreItems = (folded: PanelAction[]) => [
    ...folded.map((a) => ({ id: a.id, label: a.label, onClick: foldedClick(a) })),
    ...(props.onMakeGoal ? [{ id: 'make-goal', label: 'Make it a goal', onClick: props.onMakeGoal }] : []),
    ...(props.onRemoveFromDay
      ? [{ id: 'remove-day', label: props.removeFromDayLabel ?? 'Remove from this day', onClick: () => { void props.onRemoveFromDay?.() } }]
      : []),
    ...(props.periodActions ?? []),
  ]

  return (
    <PanelShell
      innerRef={panelRef}
      identity={
        <>
          <PanelHeader
            kind={task.isGoal ? 'Goal' : 'Task'}
            title={task.title}
            onTitleChange={props.onTitleChange}
            onClose={props.onClose}
          />
          {/* What the task IS, as labeled rows (detail pane standard,
              2026-10-19). The life area and the people used to sit lower down
              as a bare tag icon and a pair of initials; the date and the goal
              links each had a line of their own. */}
          <PanelFacts
            facts={[
              // The goal it serves first, then its when — the connected-
              // planning contract (what it is FOR leads).
              { id: 'goal', label: 'Goal', value: props.purpose ?? null },
              {
                id: 'when',
                label: 'When',
                // The saved when, then the one scheduler beside it — it is
                // the same fact, so it is no longer a chip in the action row.
                value: (
                  <>
                    {props.timingControl}
                    <SchedulePicker
                      trigger="quiet"
                      // The timing control already names a weekend ("either
                      // day"); without one, the trigger says it instead.
                      label={props.timingControl
                        ? 'Pick a date'
                        : task.weekendStart && !task.scheduledFor ? weekendLabel(task.weekendStart) : 'Schedule'}
                      flexibleWeekend
                      scheduledFor={task.scheduledFor || undefined}
                      onSchedule={props.onSchedule}
                      onReschedule={props.onReschedule}
                      onClearSchedule={props.onClearSchedule}
                      loads={dayLoads}
                    />
                  </>
                ),
              },
              {
                id: 'who',
                label: 'Who',
                value: props.familyMembers.length > 0 ? (
                  <MultiAssigneeDropdown
                    members={props.familyMembers}
                    selectedIds={task.assignedToAll ?? (task.assignedTo ? [task.assignedTo] : [])}
                    onSelect={props.onAssigneesChange}
                    size="sm"
                    showNames
                  />
                ) : null,
              },
              {
                // There is deliberately NO scope control. Scope (who can SEE
                // the task) is DERIVED from this and Who by scopeForDomain
                // (src/lib/scope.ts); a picker beside them could only disagree
                // with them, which is the state the August leak lived in.
                id: 'area',
                label: 'Area',
                value: <ContextPicker value={task.context ?? undefined} onChange={props.onContextChange} showLabel />,
              },
            ]}
          />
          {props.onBringBack && letGoNote(task) && (
            <p className="mt-2 text-sm text-neutral-500" data-testid="let-go-line">
              {letGoNote(task)}
              {' · '}
              <button
                type="button"
                className="text-neutral-700 underline underline-offset-2 hover:text-neutral-900"
                onClick={() => { void props.onBringBack?.() }}
              >
                Bring back
              </button>
            </p>
          )}
          {props.whyChain}
        </>
      }
      act={
        <>
          <PanelActions
            actions={actions}
            // One ⋯ for both the folded chips and Pin/Delete — two side by
            // side read as the same button twice (the event panel's rule).
            renderOverflow={(folded) => (
              <PanelMoreMenu
                items={moreItems(folded)}
                isPinned={props.isPinned}
                onTogglePin={props.onTogglePin}
                onDelete={props.onDelete}
                onUngroup={hasGroup ? props.onUngroup : undefined}
                onDeleteGroup={hasGroup ? props.onDeleteGroup : undefined}
              />
            )}
          />
          {waitingOpen && props.onSaveWaiting && (
            <div className="mt-2">
              <WaitingForPopover
                inline
                initialValue={task.waitingFor}
                taskId={task.id}
                onSave={(waitingFor, checkBack) => {
                  void props.onSaveWaiting?.(waitingFor, checkBack)
                  setWaitingOpen(false)
                }}
                onClear={task.isWaiting && props.onClearWaiting
                  ? () => { void props.onClearWaiting?.(); setWaitingOpen(false) }
                  : undefined}
                onCancel={() => setWaitingOpen(false)}
              />
            </div>
          )}
          {/* Wrapped, so the zone's rule sits above the card rather than on
              its rounded edge; empty when there is nothing to suggest. */}
          {props.onSendToBuy && (
            <div className="empty:hidden">
              <PanelToBuySuggestion
                taskId={task.id}
                title={task.title}
                completed={!!task.completed}
                knownPeople={[...props.familyMembers.map((m) => m.name), ...props.contacts.map((c) => c.name)]}
                onSend={props.onSendToBuy}
              />
            </div>
          )}
          <PanelAssistant taskId={task.id} />
        </>
      }
      details={
        <>
          {/* What you'll need leads the details: every resource the task
              carries, then one quiet line of what it could carry. */}
          <ReadinessSection
            task={task}
            allTasks={allTasks}
            goals={props.goals}
            contacts={props.contacts}
            files={files}
            onToggleStep={props.onToggleSubtask}
            onOpenTask={props.onOpenTask}
            onDirections={() => { setShowDirections(true); focusField('directions') }}
            onAdd={addPrep}
            opened={revealed}
          />
          {show('phone') && (<div data-prep-field="phone" className="empty:hidden">
            <PanelReach
              kind="phone"
              value={task.phoneNumber}
              onChange={props.onPhoneChange}
              autoFocus={revealed.has('phone')}
              asLink={false}
            />
          </div>)}
          {show('email') && (<div data-prep-field="email" className="empty:hidden">
            <PanelReach
              kind="email"
              value={task.email}
              onChange={props.onEmailChange}
              autoFocus={revealed.has('email')}
            />
          </div>)}
          {show('location') && (<div data-prep-field="location" className="empty:hidden">
            <PanelLocation
              location={task.location}
              locationPlaceId={task.locationPlaceId}
              title={task.title}
              showDirections={showDirections}
              onUpdateLocation={props.onUpdateLocation}
              onClearLocation={props.onClearLocation}
              directions={task.directions}
              onDirectionsChange={props.onDirectionsChange}
            />
          </div>)}
          {/* The question to bring up — the event panel's section, for a
              task. Only while it is on the list. */}
          {task.needsDiscussion && props.onUpdateDiscussion && (
            <PanelSection
              id="for-discussion"
              label="For discussion"
              preview={task.discussionNote || undefined}
            >
              <textarea
                key={task.id}
                defaultValue={task.discussionNote ?? ''}
                onBlur={(e) => {
                  if ((e.target.value || '') !== (task.discussionNote ?? '')) {
                    void props.onUpdateDiscussion?.({ needsDiscussion: true, discussionNote: e.target.value })
                  }
                }}
                aria-label="What's the question?"
                placeholder="What's the question?"
                rows={2}
                className="w-full px-2 py-1.5 text-[15px] rounded-md bg-white text-neutral-700 placeholder:text-neutral-400 shadow-[inset_0_0_0_1px_#e5e7eb] focus:outline-none focus:shadow-[inset_0_0_0_1px_#d97706] resize-none"
              />
            </PanelSection>
          )}
          {show('notes') && (<div data-prep-field="notes" className="empty:hidden">
            <PanelNotes
              key={task.id}
              label="Notes"
              notes={task.notes}
              onChange={props.onNotesChange}
              onSaveToVault={props.onSaveNoteToVault}
            />
          </div>)}
          {/* Where this row came from. Only an extracted row carries a capture,
              so the section is absent for everything typed by hand. */}
          {task.captureId && <PanelSource captureId={task.captureId} />}
          <div data-prep-field="photo" className="empty:hidden"><PanelPhotos
            hideWhenEmpty={!revealed.has('photo')}
            onContentChange={setPhotosHaveContent}
            onAttachmentsChange={setFiles}
            entityType="task"
            entityId={task.id}
            dropZoneRef={panelRef}
            entityContext={[task.title, task.notes?.split('\n')[0]].filter(Boolean).join(' — ')}
            promotions={{
              onAddPrepTask: props.onAddSubtask,
              onAddLink: props.onAddLink,
              onUseLocation: (address) => props.onUpdateLocation(address),
            }}
          /></div>
          <PanelConversations taskId={task.id} />
          {show('subtask') && (<div data-prep-field="subtask" className="empty:hidden">
            <PanelSubtasks
              subtasks={task.subtasks ?? []}
              onToggleSubtask={props.onToggleSubtask}
              onAddSubtask={props.onAddSubtask}
              onOpenSubtask={props.onOpenTask}
              onRemoveSubtask={props.onRemoveSubtask}
              onRescheduleSubtask={props.onRescheduleSubtask}
              onScheduleSubtask={props.onScheduleSubtask}
            />
          </div>)}
          {task.isGoal && <ChoicesSection goal={{ kind: 'task', id: task.id }} />}
          {task.isGoal && <GoalLogSection goal={{ kind: 'task', id: task.id }} goalContext={task.context ?? null} goalScope={task.scope ?? 'individual'} />}
          {show('person') && (<div data-prep-field="person" className="empty:hidden">
            <PanelPeople
              contact={linked.contact}
              onOpenContact={props.onOpenContact}
              contacts={props.contacts}
              onContactChange={props.onContactChange}
              onSearchContacts={props.onSearchContacts}
              onAddContact={props.onAddContact}
            />
          </div>)}
          {show('link') && <div data-prep-field="link" className="empty:hidden"><PanelLinks links={task.links} onAddLink={props.onAddLink} /></div>}
        </>
      }
      related={
        <>
          <PanelLinked
            linkedEvent={linked.linkedEvent}
            siblingTasks={linked.siblingTasks}
            onOpenEvent={props.onOpenEvent}
            onOpenTask={props.onOpenTask}
          />
          <PanelMightBeRelevant items={mightBeRelevant} onOpen={props.onOpenRelated} />
        </>
      }
      footer={
        <PanelFooter
          createdAt={task.createdAt}
          updatedAt={task.updatedAt}
          createdByName={createdByName}
        />
      }
    >
      {assistOpen && (
        <AssistDrawer
          item={{
            id: task.id,
            title: task.title,
            notes: task.notes ?? null,
            projectName: linked.project?.name ?? null,
          }}
          discuss={{ type: 'task', id: task.id, title: task.title, scope: discussScope }}
          onClose={() => setAssistOpen(false)}
          onMutate={props.onAssistMutate}
          // Sharing a private thread means MOVING the task into Family —
          // that is what makes the house able to read it. So it is only
          // offered where a domain change is not a surprise: a task already
          // tagged Family (or tagged nothing at all). A Work or Personal task
          // keeps its domain and gets no offer; the button that says what it
          // does is `Move to Family and share`, in DiscussionThread.
          onShare={
            task.context === 'family' || task.context == null
              ? () => props.onContextChange('family')
              : undefined
          }
        />
      )}
    </PanelShell>
  )
}
